import { resolveCleanupPhase } from "../phases/cleanup-phase";
import { resolveDrawPhase } from "../phases/resolve-draw-phase";
import { resolveSupplyEntry } from "../phases/supply-phase";
import {
  LANE_IDS,
  type GameEvent,
  type GameStateV5,
  type LaneState,
  type PlayerId,
  getOpponentPlayerId,
} from "../types";
import { resolvePublicBetaNoTerritoryOnEndTurn } from "./territory";
import type { TransitionResult } from "./advance-phase";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

export function resolvePublicBetaTurnStart(
  state: Readonly<GameStateV5>,
  actionSequence: number,
  startingEventOrdinal: number,
): TransitionResult {
  const playerId = state.activePlayerId;
  const events: GameEvent[] = [
    {
      type: "turn-started",
      eventId: eventId(actionSequence, startingEventOrdinal),
      actionSequence,
      playerId,
      turnNumber: state.turnNumber,
    },
  ];
  const lanes = { ...state.lanes } as Record<keyof typeof state.lanes, LaneState>;

  for (const laneId of LANE_IDS) {
    const lane = state.lanes[laneId];
    const side = lane.players[playerId];
    const commander = side.commander;
    if (lane.result !== "active" || side.status !== "active" || commander === null) continue;

    lanes[laneId] = {
      ...lane,
      players: {
        ...lane.players,
        [playerId]: {
          ...side,
          commander: {
            ...commander,
            readiness: "ready",
            deployedThisTurn: false,
          },
        },
      },
    };
    events.push({
      type: "commander-readied",
      eventId: eventId(actionSequence, startingEventOrdinal + events.length),
      actionSequence,
      playerId,
      laneId,
      commanderCardInstanceId: commander.cardInstanceId,
    });
  }

  const enteredDraw: GameStateV5 = { ...state, lanes, phase: "action-window" };
  const draw = resolveDrawPhase(enteredDraw, actionSequence, startingEventOrdinal + events.length);
  events.push(...draw.events);
  if (draw.state.schemaVersion !== 5) {
    throw new Error("Public-beta turn-start draw changed the state schema.");
  }

  const enteredSupply: GameStateV5 = {
    ...draw.state,
    phase: "supply",
    supplyStatus: { type: "not-started" },
  };
  const supply = resolveSupplyEntry(
    enteredSupply,
    actionSequence,
    startingEventOrdinal + events.length,
  );
  events.push(...supply.events);
  if (supply.state.schemaVersion !== 5) {
    throw new Error("Public-beta turn-start Supply changed the state schema.");
  }

  if (supply.state.supplyStatus.type === "awaiting-upkeep-discard") {
    return { state: { ...supply.state, phase: "supply" }, events };
  }

  const actionWindowState: GameStateV5 = { ...supply.state, phase: "action-window" };
  events.push({
    type: "action-window-opened",
    eventId: eventId(actionSequence, startingEventOrdinal + events.length),
    actionSequence,
    playerId,
    turnNumber: actionWindowState.turnNumber,
  });
  return { state: actionWindowState, events };
}

export function applyEndTurn(
  state: Readonly<GameStateV5>,
  actionSequence: number,
): TransitionResult {
  const noTerritory = resolvePublicBetaNoTerritoryOnEndTurn(state, actionSequence, 0);
  if (noTerritory !== null) return noTerritory;

  const previousPlayerId = state.activePlayerId;
  const cleanup = resolveCleanupPhase(state, actionSequence, 0);
  if (cleanup.state.schemaVersion !== 5) {
    throw new Error("Public-beta Cleanup changed the state schema.");
  }
  const activePlayerId: PlayerId = getOpponentPlayerId(previousPlayerId);
  const turnNumber = state.turnNumber + 1;
  const handedOff: GameStateV5 = {
    ...cleanup.state,
    activePlayerId,
    turnNumber,
    phase: "action-window",
  };
  const handoffEvent: GameEvent = {
    type: "turn-handed-off",
    eventId: eventId(actionSequence, cleanup.events.length),
    actionSequence,
    previousPlayerId,
    activePlayerId,
    turnNumber,
  };
  const turnStart = resolvePublicBetaTurnStart(
    handedOff,
    actionSequence,
    cleanup.events.length + 1,
  );
  return {
    state: turnStart.state,
    events: [...cleanup.events, handoffEvent, ...turnStart.events],
  };
}
